import { AnimationGroup, AssetContainer, Node, SceneLoader, TransformNode, type Scene } from "babylonjs";
import "babylonjs-loaders"; // register the glTF loader.
import { IAssetBase } from "./loader";
import { IS_BABYLON_NATIVE_JSCORE } from "../utils/utils";

// Per-scene cache of loaded GLB AssetContainers (templates), keyed by URL. A
// model is loaded ONCE and instantiated as cheap copies sharing geometry +
// materials — important for the editor (duplicate elements of one model) and any
// boilerplate scene that reuses a model. Disposed via disposeModelCache(scene)
// when the content owning them is torn down. Lives in web-core because it is
// rendering/loading shared between the editor and the boilerplates.
//
// loadModelInstance / unloadModelInstance follow the same IAsset shape as the
// other loaders in loader.ts (load mutates the asset, unload tears the instance
// down). The scene-level template cache (getModelContainer / disposeModelCache)
// is a separate concern: a template is SHARED across many instance-assets, so it
// can't live on a per-asset object.

const sceneContainers = new WeakMap<Scene, Map<string, AssetContainer>>();
const sceneLoading = new WeakMap<Scene, Map<string, Promise<AssetContainer>>>();

function cacheFor(scene: Scene): Map<string, AssetContainer> {
  let cache = sceneContainers.get(scene);
  if (!cache) {
    cache = new Map();
    sceneContainers.set(scene, cache);
  }
  return cache;
}

function loadingFor(scene: Scene): Map<string, Promise<AssetContainer>> {
  let loading = sceneLoading.get(scene);
  if (!loading) {
    loading = new Map();
    sceneLoading.set(scene, loading);
  }
  return loading;
}

// Get (or load + cache) the AssetContainer for a model URL. Concurrent requests
// for the same URL share one load.
export async function getModelContainer(path: string, scene: Scene, extension = ".glb"): Promise<AssetContainer> {
  if (IS_BABYLON_NATIVE_JSCORE) {
    // Draco GLBs hang forever on native with Babylon's stock decoder
    // bootstrap — see loader.ts loadModel for the full story.
    const { configureNativeDracoDecoder } = await import("./loader.draco.native");
    configureNativeDracoDecoder();
  }
  const cache = cacheFor(scene);
  const cached = cache.get(path);
  if (cached) return cached;

  const loading = loadingFor(scene);
  let inFlight = loading.get(path);
  if (!inFlight) {
    inFlight = SceneLoader.LoadAssetContainerAsync("", path, scene, undefined, extension)
      .then(container => {
        cache.set(path, container);
        loading.delete(path);
        return container;
      })
      .catch(err => {
        loading.delete(path);
        throw err;
      });
    loading.set(path, inFlight);
  }
  return inFlight;
}

// A model-instance asset: the same IAsset shape as the loaders in loader.ts. The
// result (the instantiated clone's roots + its own animation-group clones) is
// written back onto the asset, mirroring loadModel/loadTexture/loadVideo.
export interface IAssetModelInstance extends IAssetBase {
  extension?: string; // optional explicit extension (e.g. ".glb") for extensionless URLs.
  rootNodes?: Node[];
  animationGroups?: AnimationGroup[];
}

// Instantiate a cached model under rootNode — a cheap clone that shares the
// container's geometry + materials (cloneMaterials=false). Sets asset.rootNodes
// + asset.animationGroups (the instance's own animation-group clones).
export async function loadModelInstance(
  asset: IAssetModelInstance,
  scene: Scene,
  rootNode: TransformNode,
): Promise<void> {
  const container = await getModelContainer(asset.path, scene, asset.extension ?? ".glb");
  const entries = container.instantiateModelsToScene(name => `${rootNode.name}_${name}`, false);
  for (const node of entries.rootNodes) node.parent = rootNode;
  asset.rootNodes = entries.rootNodes;
  asset.animationGroups = entries.animationGroups;
}

// Dispose this instance's nodes + animation-group clones — but NOT the shared
// template's geometry/materials (those belong to the cached AssetContainer and
// are reused by sibling instances; disposed by disposeModelCache). Symmetric
// counterpart to loadModelInstance.
export function unloadModelInstance(asset: IAssetModelInstance): void {
  if (asset.animationGroups) {
    for (const group of asset.animationGroups) {
      group.stop();
      group.dispose();
    }
    asset.animationGroups = undefined;
  }
  if (asset.rootNodes) {
    for (const node of asset.rootNodes) node.dispose(false, false); // recurse children, keep materials
    asset.rootNodes = undefined;
  }
}

// Dispose all cached containers (templates + their shared materials) for a scene.
// Call AFTER the instances built from them have been disposed.
export function disposeModelCache(scene: Scene): void {
  const cache = sceneContainers.get(scene);
  if (cache) {
    for (const container of cache.values()) container.dispose();
    cache.clear();
    sceneContainers.delete(scene);
  }
  sceneLoading.delete(scene);
}
